import type { Backup } from './import';

type Extra = {
  last?: string;
  birthName?: string;
  birthPlace?: string;
  died?: string;
  restingPlace?: string;
  occupation?: string;
  residence?: string;
  story?: string;
  email?: string;
  phone?: string;
  instagram?: string;
  facebook?: string;
  linkedin?: string;
  twitter?: string;
  website?: string;
};

/**
 * The Whitfield sample family, expressed as a prototype backup so it flows through the normal importer.
 *
 * Twenty-nine people over five generations (1894–2022), built to show what a full tree looks like:
 * in-laws from two other families, a widow who remarries (a stepfather, a stepbrother and a
 * half-sister), and first and second cousins. Everyone has dates, places and a story, and the profiles
 * get richer with each generation: the great-grandparents have a life story and a resting place, the
 * grandparents' generation adds an email and phone, and the younger generations carry a full profile
 * (email, phone, Instagram, Facebook, LinkedIn, X and a website, wherever it suits their work).
 *
 * Siblings are written eldest first, and the families are laid out around Harold and Margaret, the main
 * grandparents, so keep birth dates in order when adding anyone: the canvas sorts siblings by age.
 *
 * Portraits are not part of the backup: the web app ships them as static files at
 * `apps/web/public/sample/portraits/<id>.jpg`, keyed by the ids used here.
 */
export function sampleBackup(): Backup {
  let counter = 0;
  const p = (
    id: string,
    first: string,
    gender: 'female' | 'male',
    born: string,
    parents: string[] = [],
    spouses: string[] = [],
    extra: Extra = {},
  ) => {
    return {
      id,
      created: 1_700_000_000_000 + counter++ * 1000,
      first,
      last: extra.last ?? 'Whitfield',
      birthName: extra.birthName ?? '',
      gender,
      born,
      birthPlace: extra.birthPlace ?? '',
      living: !extra.died,
      died: extra.died ?? '',
      restingPlace: extra.restingPlace ?? '',
      occupation: extra.occupation ?? '',
      residence: extra.residence ?? '',
      story: extra.story ?? '',
      email: extra.email ?? '',
      phone: extra.phone ?? '',
      instagram: extra.instagram ?? '',
      facebook: extra.facebook ?? '',
      linkedin: extra.linkedin ?? '',
      twitter: extra.twitter ?? '',
      website: extra.website ?? '',
      parents,
      spouses,
    };
  };
  const lawnswood = 'Lawnswood Cemetery, Leeds';

  // Written left to right, generation by generation: the canvas starts from this order, so the
  // main family (Harold and Margaret) lands in the middle with the in-laws and cousins at the sides.
  const people = [
    p('walter', 'Walter', 'male', '3 Feb 1894', [], ['edith'], {
      birthPlace: 'Leeds, Yorkshire',
      died: '12 Nov 1971',
      restingPlace: lawnswood,
      occupation: 'Tram driver',
      residence: 'Headingley, Leeds',
      story:
        'Drove the Headingley tram for thirty-one years and could name every stop in his sleep. Gassed at Passchendaele in 1917, he came home, married Edith within the year and never spoke about the war except to say the tram was the quietest job in Yorkshire. He kept a brass pocket watch that has since travelled down four generations, and on his last day of work the whole route stood to wave him through.',
    }),
    p('edith', 'Edith', 'female', '21 Jun 1898', [], ['walter'], {
      birthName: 'Brooks',
      birthPlace: 'Harrogate, Yorkshire',
      died: '2 Mar 1980',
      restingPlace: lawnswood,
      occupation: 'Dressmaker',
      residence: 'Headingley, Leeds',
      story:
        'Ran a dressmaking business from the front room and stitched wedding gowns for half of Headingley, often paid in eggs and gossip. During the war she turned blackout curtains into christening robes. Her parkin recipe, written on the back of a pattern envelope, is still made every Bonfire Night.',
    }),
    p('arthur', 'Arthur', 'male', '17 Jan 1930', [], ['rose'], {
      last: 'Ashworth',
      birthPlace: 'York',
      died: '25 Feb 2015',
      restingPlace: 'Fulford Cemetery, York',
      occupation: 'Railway engineer',
      residence: 'Acomb, York',
      story:
        'Worked the York to King’s Cross line and once took his family to watch the Flying Scotsman being steamed. A model locomotive sat on his mantelpiece for sixty years, and he could tell any engine by its whistle. He and Rose celebrated sixty years of marriage with a day trip on a steam train.',
      email: 'arthur.ashworth@example.com',
    }),
    p('rose', 'Rose', 'female', '6 Apr 1933', [], ['arthur'], {
      last: 'Ashworth',
      birthName: 'Penhaligon',
      birthPlace: 'Penzance, Cornwall',
      occupation: 'Seamstress (retired)',
      residence: 'Acomb, York',
      story:
        'Moved north from Cornwall at nineteen and never lost her accent. At ninety-three she still hems her great-grandchildren’s school trousers, tells the story of the winter the sea froze at Marazion, and insists on a proper cream tea whenever anyone visits. She keeps a landline and answers it on the second ring.',
      phone: '+44 1904 555 0142',
    }),
    p('harold', 'Harold', 'male', '8 May 1921', ['walter', 'edith'], ['margaret'], {
      birthPlace: 'Leeds, Yorkshire',
      died: '19 Mar 2004',
      restingPlace: lawnswood,
      occupation: 'Master carpenter',
      residence: 'Headingley, Leeds',
      story:
        'Built the family home with his own hands and never threw away a nail: they were straightened and kept in a jam jar. He made every grandchild a wooden toy chest with their name burned into the lid, and fixed half the pews in St Michael’s for nothing. When Walter died, the pocket watch passed to Harold, and later to Thomas.',
      email: 'harold.whitfield@example.com',
    }),
    p('margaret', 'Margaret', 'female', '2 Oct 1924', [], ['harold'], {
      birthName: 'Hale',
      birthPlace: 'Sheffield, Yorkshire',
      died: '11 Jul 2011',
      restingPlace: lawnswood,
      occupation: 'Schoolteacher',
      residence: 'Headingley, Leeds',
      story:
        'Taught infants for thirty years and could silence a room with one raised eyebrow; three generations of Headingley remember her as Miss Hale. She wrote a small poem for every grandchild’s birthday and kept them all in a biscuit tin, which now sits on Daniel’s shelf. She and Harold danced at the Town Hall every Saturday until he was eighty.',
    }),
    p('frank', 'Frank', 'male', '9 Sep 1923', ['walter', 'edith'], ['dorothy'], {
      birthPlace: 'Leeds, Yorkshire',
      died: '17 Apr 1990',
      restingPlace: lawnswood,
      occupation: 'Bookbinder',
      residence: 'Roundhay, Leeds',
      story:
        'Harold’s younger brother and the family’s quiet craftsman. He rebound the family Bible in 1962 and hid a pressed violet inside the back cover, which his granddaughter found forty years later. He taught himself Latin in the evenings to read the old parish registers.',
    }),
    p('dorothy', 'Dorothy', 'female', '14 Jan 1925', [], ['frank'], {
      birthName: 'Marsh',
      birthPlace: 'Bradford, Yorkshire',
      died: '30 Aug 2008',
      restingPlace: lawnswood,
      occupation: 'Nurse',
      residence: 'Roundhay, Leeds',
      story:
        'Trained at Leeds General Infirmary and nursed through the bitter winter of 1947, when the wards ran out of coal. Her letters were famously written right into the margins to save paper, and Ronald keeps a shoebox of them. She is the reason three of her great-grandchildren are in healthcare.',
    }),
    p('raj', 'Raj', 'male', '7 Dec 1955', [], ['meera'], {
      last: 'Nair',
      birthPlace: 'Kochi, Kerala',
      occupation: 'Pharmacist',
      residence: 'Oadby, Leicester',
      story:
        'Left Kochi at twenty-two with one suitcase and opened a pharmacy in Leicester. Arjun and Priya’s father and a devoted, competitive Scrabble player who plays Malayalam words and insists they count.',
      email: 'raj.nair@example.com',
      phone: '+44 7700 900161',
      facebook: 'https://facebook.com/raj.nair.sample',
    }),
    p('meera', 'Meera', 'female', '25 Sep 1958', [], ['raj'], {
      last: 'Nair',
      birthName: 'Menon',
      birthPlace: 'Thrissur, Kerala',
      occupation: 'Accountant',
      residence: 'Oadby, Leicester',
      story:
        'Keeps the books for half the shops on her street and the recipes for a whole family. Her payasam is requested at every celebration, and she has promised to write the recipe down once she works out the measurements.',
      email: 'meera.nair@example.com',
      phone: '+44 7700 900162',
      facebook: 'https://facebook.com/meera.nair.sample',
      linkedin: 'https://www.linkedin.com/in/meera-nair-sample',
    }),
    p('helen', 'Helen', 'female', '27 Jul 1957', ['arthur', 'rose'], ['thomas'], {
      birthName: 'Ashworth',
      birthPlace: 'York',
      occupation: 'Librarian',
      residence: 'Clifton, Bristol',
      story:
        'Forty years in public libraries and still reads a novel a week. She started the Sunday letters that keep four households in touch, and has never once forgotten a birthday.',
      email: 'helen.whitfield@example.com',
      phone: '+44 7700 900151',
      facebook: 'https://facebook.com/helen.whitfield.sample',
    }),
    p('thomas', 'Thomas', 'male', '14 Nov 1955', ['harold', 'margaret'], ['helen'], {
      birthPlace: 'Leeds, Yorkshire',
      occupation: 'Architect',
      residence: 'Clifton, Bristol',
      story:
        'Designed libraries and community centres across the South West, and met Helen in one of them. Carries Walter’s brass pocket watch and has promised it to Daniel. Still sketches every building he walks past.',
      email: 'thomas.whitfield@example.com',
      phone: '+44 7700 900152',
      linkedin: 'https://www.linkedin.com/in/thomas-whitfield-sample',
      website: 'https://whitfieldarchitects.example.com',
    }),
    p('anne', 'Anne', 'female', '3 Mar 1958', ['harold', 'margaret'], ['samuel'], {
      birthPlace: 'Leeds, Yorkshire',
      occupation: 'GP',
      residence: 'Penarth, Cardiff',
      story:
        'A family doctor for thirty years and the unofficial medical adviser at every wedding. Widowed at thirty-three, she raised Sophie alone for two years before marrying Samuel in 1993, gaining a stepson and, two years later, a daughter.',
      email: 'anne.adeyemi@example.com',
      phone: '+44 7700 900153',
      facebook: 'https://facebook.com/anne.adeyemi.sample',
      linkedin: 'https://www.linkedin.com/in/anne-adeyemi-sample',
    }),
    p('samuel', 'Samuel', 'male', '30 Jul 1955', [], ['anne'], {
      last: 'Adeyemi',
      birthPlace: 'Lagos, Nigeria',
      occupation: 'Hospital pharmacist',
      residence: 'Penarth, Cardiff',
      story:
        'Came from Lagos to Cardiff to study pharmacy and stayed for the rain. Became Sophie’s stepfather and then Ruth’s father, and still holds the family chess championship, which he has never lost on Boxing Day.',
      email: 'samuel.adeyemi@example.com',
      phone: '+44 7700 900154',
      facebook: 'https://facebook.com/samuel.adeyemi.sample',
    }),
    p('ronald', 'Ronald', 'male', '26 Jan 1950', ['frank', 'dorothy'], ['june'], {
      birthPlace: 'Leeds, Yorkshire',
      occupation: 'Chartered surveyor',
      residence: 'Harrogate, Yorkshire',
      story:
        'Thomas’s first cousin and the keeper of the Whitfield family photographs from before the war. He labels every print in pencil on the back and hosts the cousins’ reunion every August, with a cricket match nobody takes seriously.',
      email: 'ronald.whitfield@example.com',
      phone: '+44 7700 900141',
      facebook: 'https://facebook.com/ronald.whitfield.sample',
    }),
    p('june', 'June', 'female', '3 Jun 1952', [], ['ronald'], {
      birthName: 'Pike',
      birthPlace: 'Ilkley, Yorkshire',
      occupation: 'Florist',
      residence: 'Harrogate, Yorkshire',
      story:
        'Ran a flower shop on Harrogate’s Montpellier Parade for twenty years. Every wedding bouquet in the family since 1975 has been hers, and she still turns up at christenings with a posy in her handbag.',
      email: 'june.whitfield@example.com',
      phone: '+44 7700 900142',
      facebook: 'https://facebook.com/june.whitfield.sample',
      instagram: '@junes.posies.sample',
    }),
    p('arjun', 'Arjun', 'male', '14 Jan 1983', ['raj', 'meera'], [], {
      last: 'Nair',
      birthPlace: 'Leicester',
      occupation: 'Marine biologist',
      residence: 'Plymouth, Devon',
      story:
        'Studies seagrass meadows off the Devon coast and sends the children photographs of seahorses. Priya’s older brother, and the uncle who taught Leo to identify a hermit crab.',
      email: 'arjun.nair@example.com',
      phone: '+44 7700 900171',
      instagram: '@arjun.under.the.sea.sample',
      facebook: 'https://facebook.com/arjun.nair.sample',
      linkedin: 'https://www.linkedin.com/in/arjun-nair-sample',
      twitter: '@seagrass_arjun_sample',
      website: 'https://seagrassproject.example.org',
    }),
    p('priya', 'Priya', 'female', '18 Mar 1986', ['raj', 'meera'], ['daniel'], {
      last: 'Whitfield',
      birthName: 'Nair',
      birthPlace: 'Leicester',
      occupation: 'Paediatrician',
      residence: 'Bishopston, Bristol',
      story:
        'Spends her days with small, brave patients and her evenings arguing about the right way to make chai. Daniel’s wife and Leo and Maya’s mum, and the one who keeps the family calendar from collapsing.',
      email: 'priya.whitfield@example.com',
      phone: '+44 7700 900172',
      instagram: '@dr.priya.chai.sample',
      facebook: 'https://facebook.com/priya.whitfield.sample',
      linkedin: 'https://www.linkedin.com/in/priya-whitfield-sample',
      twitter: '@priya_paeds_sample',
      website: 'https://bristolchildrens.example.org/priya',
    }),
    p('daniel', 'Daniel', 'male', '9 Jun 1984', ['thomas', 'helen'], ['priya'], {
      birthPlace: 'Bristol',
      occupation: 'Software engineer',
      residence: 'Bishopston, Bristol',
      story:
        'The family’s self-appointed archivist, forever scanning old photographs at the kitchen table and building the tree you are looking at. Plays cricket badly and cheerfully, and is saving Walter’s pocket watch for Leo.',
      email: 'daniel.whitfield@example.com',
      phone: '+44 7700 900173',
      instagram: '@daniel.scans.sample',
      facebook: 'https://facebook.com/daniel.whitfield.sample',
      linkedin: 'https://www.linkedin.com/in/daniel-whitfield-sample',
      twitter: '@dan_whitfield_sample',
      website: 'https://danielwhitfield.example.com',
    }),
    p('lucy', 'Lucy', 'female', '23 Jan 1987', ['thomas', 'helen'], ['mark'], {
      birthPlace: 'Bristol',
      occupation: 'Journalist',
      residence: 'Bath, Somerset',
      story:
        'Covers the arts for a regional paper and has interviewed more potters than anyone alive. Writes the family newsletter nobody asked for and everybody reads.',
      email: 'lucy.delaney@example.com',
      phone: '+44 7700 900174',
      instagram: '@lucywritesbath.sample',
      facebook: 'https://facebook.com/lucy.delaney.sample',
      linkedin: 'https://www.linkedin.com/in/lucy-whitfield-sample',
      twitter: '@lucy_arts_sample',
      website: 'https://lucywrites.example.com',
    }),
    p('mark', 'Mark', 'male', '3 Oct 1985', [], ['lucy'], {
      last: 'Delaney',
      birthPlace: 'Cork, Ireland',
      occupation: 'Chef',
      residence: 'Bath, Somerset',
      story:
        'Runs a twelve-table bistro and brings the soup to every family occasion. Ella’s and Noah’s dad, and Cork’s loudest supporter in the room.',
      email: 'mark.delaney@example.com',
      phone: '+44 7700 900175',
      instagram: '@marks.soup.sample',
      facebook: 'https://facebook.com/mark.delaney.sample',
      twitter: '@mark_cooks_sample',
      website: 'https://twelvetables.example.com',
    }),
    p('sophie', 'Sophie', 'female', '4 Sep 1986', ['anne'], [], {
      last: 'Carter',
      birthPlace: 'Cardiff',
      occupation: 'Landscape architect',
      residence: 'Edinburgh',
      story:
        'Anne’s daughter. Her father, James, died when she was five and had taught her to ride a bike a few weeks before; she still cycles everywhere. Designs public gardens and calls it his engineering with better planting.',
      email: 'sophie.carter@example.com',
      phone: '+44 7700 900181',
      instagram: '@sophie.plants.cities.sample',
      facebook: 'https://facebook.com/sophie.carter.sample',
      linkedin: 'https://www.linkedin.com/in/sophie-carter-sample',
      twitter: '@sophiecarter_land_sample',
      website: 'https://sophiecarter.example.com',
    }),
    p('tobi', 'Tobi', 'male', '12 Feb 1990', ['samuel'], [], {
      last: 'Adeyemi',
      birthPlace: 'Cardiff',
      occupation: 'Physiotherapist',
      residence: 'London',
      story:
        'Samuel’s son from an earlier relationship. He was three when Anne joined the family and says he gained a stepmother, a stepsister and, later, a little sister. Runs a half-marathon every spring and drags the cousins along.',
      email: 'tobi.adeyemi@example.com',
      phone: '+44 7700 900182',
      instagram: '@tobi.moves.sample',
      facebook: 'https://facebook.com/tobi.adeyemi.sample',
      linkedin: 'https://www.linkedin.com/in/tobi-adeyemi-sample',
      twitter: '@tobi_physio_sample',
      website: 'https://moveswithtobi.example.com',
    }),
    p('ruth', 'Ruth', 'female', '19 Dec 1995', ['anne', 'samuel'], [], {
      last: 'Adeyemi',
      birthPlace: 'Cardiff',
      occupation: 'Junior doctor',
      residence: 'Cardiff',
      story:
        'Anne and Samuel’s daughter and the only cousin who followed Anne into medicine. Half-sister to Sophie and Tobi’s little sister, and loves being both. Plays the cello badly on night shifts.',
      email: 'ruth.adeyemi@example.com',
      phone: '+44 7700 900183',
      instagram: '@ruth.on.call.sample',
      facebook: 'https://facebook.com/ruth.adeyemi.sample',
      linkedin: 'https://www.linkedin.com/in/ruth-adeyemi-sample',
      twitter: '@ruth_on_call_sample',
      website: 'https://ruthadeyemi.example.com',
    }),
    p('beth', 'Beth', 'female', '18 Oct 1980', ['ronald', 'june'], [], {
      birthPlace: 'Leeds, Yorkshire',
      occupation: 'Veterinary surgeon',
      residence: 'Leeds, Yorkshire',
      story:
        'Daniel’s second cousin, who found him through the family tree and turned out to live four miles from his grandfather’s old house. Runs a small-animal practice with an unreasonable number of rescue greyhounds in the waiting room.',
      email: 'beth.whitfield@example.com',
      phone: '+44 7700 900143',
      instagram: '@beth.and.the.hounds.sample',
      facebook: 'https://facebook.com/beth.whitfield.sample',
      linkedin: 'https://www.linkedin.com/in/beth-whitfield-sample',
      twitter: '@bethvet_sample',
      website: 'https://hillcrestvets.example.com',
    }),
    p('leo', 'Leo', 'male', '21 Aug 2016', ['daniel', 'priya'], [], {
      birthPlace: 'Bristol',
      occupation: 'Schoolboy',
      residence: 'Bishopston, Bristol',
      story:
        'Collects fossils, asks questions faster than anyone can answer them, and plans to be a palaeontologist. Daniel is saving the pocket watch for him, and Leo has already drawn the museum it will go in.',
    }),
    p('maya', 'Maya', 'female', '5 Apr 2019', ['daniel', 'priya'], [], {
      birthPlace: 'Bristol',
      occupation: 'Schoolgirl',
      residence: 'Bishopston, Bristol',
      story:
        'Draws maps of imaginary islands and has firm opinions about pancakes. Wants to sail to every one of them with her cousin Ella.',
    }),
    p('ella', 'Ella', 'female', '11 Jul 2019', ['lucy', 'mark'], [], {
      last: 'Delaney',
      birthPlace: 'Bath, Somerset',
      occupation: 'Schoolgirl',
      residence: 'Bath, Somerset',
      story:
        'Stirs the soup at her father’s restaurant on a step stool and has already announced she will take over the kitchen.',
    }),
    p('noah', 'Noah', 'male', '2 Mar 2022', ['lucy', 'mark'], [], {
      last: 'Delaney',
      birthPlace: 'Bath, Somerset',
      residence: 'Bath, Somerset',
      story:
        'The newest Whitfield descendant: loud, curious and fond of collecting spoons. He is the reason the family chat never goes quiet.',
    }),
  ];

  return {
    app: 'lineage',
    version: 2,
    tree: { title: 'The Whitfield Family', meId: 'daniel', people: Object.fromEntries(people.map((x) => [x.id, x])) },
    photos: {},
  } as Backup;
}
